/* eslint-disable @typescript-eslint/camelcase */
import i18n from 'i18n';
import { messagesRequest } from 'modules/messages/actions';
import { modalClose } from 'modules/modal/actions';
import { push } from 'react-router-redux';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import ApplicationState from 'store/interfaces';
import {
  editRoleFulfilled,
  editRoleRejected,
  editRoleRequest,
  getAllObjectsFulfilled,
  getAllObjectsRejected,
  getAllObjectsRequest,
  getAllRolesFulfilled,
  getAllRolesRejected,
  getAllRolesRequest,
  getObjectsAvailableFulfilled,
  getObjectsAvailableRejected,
  getObjectsAvailableRequest,
  getObjectsFulfilled,
  getObjectsRejected,
  getObjectsRequest,
  getPermissionsFulfilled,
  getPermissionsRejected,
  getPermissionsRequest,
  getRoleInfoFulfilled,
  getRoleInfoRejected,
  getRoleInfoRequest,
  registerObjectsFulfilled,
  registerObjectsRejected,
  registerObjectsRequest,
  registerPermissionsFulfilled,
  registerPermissionsRejected,
  registerPermissionsRequest,
  registerRoleFulfilled,
  registerRoleRejected,
  registerRoleRequest,
  removeObjectsFulfilled,
  removeObjectsRejected,
  removeObjectsRequest,
  removeRolesFulfilled,
  removeRolesRejected,
  removeRolesRequest,
  toggleAddAllObjectsFulfilled,
  toggleAddAllObjectsRejected,
  toggleAddAllObjectsRequest,
  cloneRoleRejected,
  cloneRoleFulfilled,
  cloneRoleRequest,
} from './actions';
import endpoints, { api } from './endpoints';

function* registerRoleRequestSaga({ payload }) {
  try {
    const { data } = yield api.post({
      url: endpoints.registerRole(),
      data: payload,
    });

    return yield all([
      put(registerRoleFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('GROUPS_PAGE.ADDED_GROUP_KEY'),
        })
      ),
      put(modalClose()),
      put(push(`/groups/${data.role_uuid}`)),
    ]);
  } catch (error) {
    return yield put(registerRoleRejected());
  }
}

function* editRoleRequestSaga({ payload }) {
  try {
    const { data } = yield api.put({
      url: endpoints.editRole(payload.role_uuid),
      data: payload.data,
    });

    return yield all([
      put(editRoleFulfilled(data)),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('GROUPS_PAGE.UPDATED_GROUP_KEY'),
        })
      ),
      put(modalClose()),
    ]);
  } catch (error) {
    return yield put(editRoleRejected());
  }
}

function* getAllRolesRequestSaga({ payload }) {
  const { query, operator_role } = payload;
  try {
    const { data } = yield api.get({
      url: endpoints.getAllRoles(operator_role, query),
    });

    return yield put(getAllRolesFulfilled(data));
  } catch (error) {
    return yield put(getAllRolesRejected());
  }
}

function* removeRolesRequestSaga({ payload }) {
  try {
    yield api.post({
      url: endpoints.removeRoles(),
      data: {
        uuid: payload,
      },
    });

    return yield all([
      put(removeRolesFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('GROUPS_PAGE.REMOVED_GROUP_KEY'),
        })
      ),
    ]);
  } catch (error) {
    return yield put(removeRolesRejected());
  }
}

function* getRoleInfoRequestSaga({ payload }) {
  const { role_uuid } = payload;

  try {
    const { data } = yield api.get({
      url: endpoints.getRoleInfo(role_uuid),
    });

    return yield put(getRoleInfoFulfilled(data));
  } catch (error) {
    return yield put(getRoleInfoRejected());
  }
}

function* getAllObjectsRequestSaga({ payload }) {
  const { role_uuid } = payload;

  try {
    const [company, partner, environment, client, operator] = yield all([
      call(api.get, {
        url: endpoints.getObjects(role_uuid, 'company', payload?.query),
      }),
      call(api.get, {
        url: endpoints.getObjects(role_uuid, 'partner', payload?.query),
      }),
      call(api.get, {
        url: endpoints.getObjects(role_uuid, 'environment', payload?.query),
      }),
      call(api.get, {
        url: endpoints.getObjects(role_uuid, 'client', payload?.query),
      }),
      call(api.get, {
        url: endpoints.getObjects(role_uuid, 'operator', payload?.query),
      }),
    ]);

    const data = {
      company: { selected: company.data, loading: false },
      partner: { selected: partner.data, loading: false },
      environment: { selected: environment.data, loading: false },
      client: { selected: client.data, loading: false },
      operator: { selected: operator.data, loading: false },
    };

    return yield put(getAllObjectsFulfilled(data));
  } catch (error) {
    return yield put(getAllObjectsRejected());
  }
}

function* getObjectsRequestSaga({ payload }) {
  const { role_uuid, entity } = payload;

  try {
    const { data } = yield api.get({
      url: endpoints.getObjects(role_uuid, entity, payload?.query),
    });

    return yield put(
      getObjectsFulfilled({
        [entity]: { selected: data, loading: false },
      })
    );
  } catch (error) {
    return yield put(getObjectsRejected());
  }
}

function* getObjectsAvailableRequestSaga({ payload }) {
  const { role_uuid, entity } = payload;

  try {
    const { data } = yield api.get({
      url: endpoints.getObjectsAvailable(role_uuid, entity, payload?.query),
    });

    return yield put(
      getObjectsAvailableFulfilled({
        [entity]: { available: data, loading: false },
      })
    );
  } catch (error) {
    return yield put(getObjectsAvailableRejected());
  }
}

function* registerObjectsRequestSaga({ payload }) {
  const { role_uuid, entity, objects } = payload;

  try {
    yield api.post({
      url: endpoints.registerObjects(role_uuid, entity),
      data: { uuid: objects },
    });

    return yield all([
      put(registerObjectsFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('GROUPS_PAGE.UPDATED_GROUP_KEY'),
        })
      ),
      put(getObjectsRequest({ role_uuid, entity })),
    ]);
  } catch (error) {
    return yield put(registerObjectsRejected());
  }
}

function* removeObjectsRequestSaga({ payload }) {
  const { role_uuid, entity, objects } = payload;

  try {
    yield api.post({
      url: endpoints.removeObjects(role_uuid, entity),
      data: { uuid: objects },
    });

    return yield all([
      put(removeObjectsFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('GROUPS_PAGE.REMOVED_ITEMS_KEY'),
        })
      ),
      put(getObjectsRequest({ role_uuid, entity })),
    ]);
  } catch (error) {
    return yield put(removeObjectsRejected());
  }
}

function* getPermissionsRequestSaga({ payload }) {
  const { role_uuid, entity } = payload;

  try {
    const selected = yield call(api.get, {
      url: endpoints.getPermissions(role_uuid, entity),
    });

    const permissionsSelected: string[] =
      selected.data.codes
        ?.filter(item => item?.selected)
        ?.map(item => item?.name) || [];

    return yield put(
      getPermissionsFulfilled({
        [entity]: {
          available: selected.data.codes,
          selected: permissionsSelected,
          loading: false,
        },
      })
    );
  } catch (error) {
    return yield put(getPermissionsRejected());
  }
}

function* registerPermissionsRequestSaga({ payload }) {
  const { role_uuid, entity, permissions } = payload;
  const { permission } = yield select((state: ApplicationState) => state);

  try {
    yield api.put({
      url: endpoints.updatePermissions(role_uuid, entity),
      data: { code: permissions },
    });
    return yield all([
      put(
        registerPermissionsFulfilled({
          [entity]: {
            ...permission.permissions[entity],
            selected: permissions,
            loading: false,
          },
        })
      ),
    ]);
  } catch (error) {
    return yield put(registerPermissionsRejected());
  }
}

function* toggleAddAllObjectsRequestSaga({ payload }) {
  const { role_uuid, entity, action } = payload;

  try {
    yield api.post({
      url: endpoints.toggleAddAllObjects(role_uuid, entity, action),
    });

    return yield all([
      put(toggleAddAllObjectsFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('GROUPS_PAGE.UPDATED_GROUP_KEY'),
        })
      ),
      put(getRoleInfoRequest({ role_uuid })),
      put(getObjectsRequest({ role_uuid, entity })),
    ]);
  } catch (error) {
    return yield put(toggleAddAllObjectsRejected());
  }
}

function* cloneRoleRequestSaga({ payload }) {
  const { role_uuid, operator_role, query, ...data } = payload;

  try {
    yield api.post({
      url: endpoints.cloneRole(role_uuid),
      data,
    });

    return yield all([
      put(cloneRoleFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('GROUPS_PAGE.GROUP_CLONE_MESSAGE_KEY'),
        })
      ),
      put(modalClose()),
      put(getAllRolesRequest({ operator_role, query })),
    ]);
  } catch (error) {
    return yield put(cloneRoleRejected());
  }
}

export default all([
  takeLatest(registerRoleRequest, registerRoleRequestSaga),
  takeLatest(editRoleRequest, editRoleRequestSaga),
  takeLatest(getAllRolesRequest, getAllRolesRequestSaga),
  takeLatest(removeRolesRequest, removeRolesRequestSaga),

  takeLatest(getRoleInfoRequest, getRoleInfoRequestSaga),

  takeLatest(getAllObjectsRequest, getAllObjectsRequestSaga),
  takeLatest(getObjectsRequest, getObjectsRequestSaga),
  takeLatest(getObjectsAvailableRequest, getObjectsAvailableRequestSaga),
  takeLatest(registerObjectsRequest, registerObjectsRequestSaga),
  takeLatest(removeObjectsRequest, removeObjectsRequestSaga),

  takeLatest(getPermissionsRequest, getPermissionsRequestSaga),
  takeLatest(registerPermissionsRequest, registerPermissionsRequestSaga),

  takeLatest(toggleAddAllObjectsRequest, toggleAddAllObjectsRequestSaga),
  takeLatest(cloneRoleRequest, cloneRoleRequestSaga),
]);
